import { Languages } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Dialog, ErrorBox, Field, Select } from "./ui";

/** Translates the text of one locale into the other locales with the app LLM. */
export function TranslateDialog({
  open,
  onOpenChange,
  locales,
  texts,
  onTranslate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  locales: string[];
  /** Current text per locale (unsaved edits included). */
  texts: Record<string, string>;
  onTranslate: (body: { sourceLocale: string; sourceText: string; locales: string[] }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const withText = locales.filter((l) => texts[l]?.trim());
  const [source, setSource] = useState("");
  const [targets, setTargets] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (!open) return;
    const first = withText[0] ?? "";
    setSource(first);
    setTargets(locales.filter((l) => l !== first));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const sourceText = texts[source] ?? "";

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await onTranslate({ sourceLocale: source, sourceText, locales: [source, ...targets] });
      toast.success(t("build.translated", { count: targets.length }));
      onOpenChange(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("build.fillTranslate")}
      description={t("build.translateHelp")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={run} loading={busy} disabled={!sourceText.trim() || targets.length === 0}>
            <Languages className="h-4 w-4" />
            {t("build.translate")}
          </Button>
        </>
      }
    >
      {withText.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("build.translateNothing")}</p>
      ) : (
        <div className="space-y-4">
          <Field label={t("build.sourceLocale")} htmlFor="tr-source">
            <Select
              id="tr-source"
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setTargets(locales.filter((l) => l !== e.target.value));
              }}
            >
              {withText.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <blockquote className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border-l-2 border-brand-500 bg-muted/60 px-3 py-2 text-sm">{sourceText}</blockquote>
          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-sm font-medium">{t("build.targetLocales")}</legend>
            {locales
              .filter((l) => l !== source)
              .map((l) => (
                <label key={l} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-sky-600"
                    checked={targets.includes(l)}
                    onChange={(e) => setTargets((ts) => (e.target.checked ? [...ts, l] : ts.filter((x) => x !== l)))}
                  />
                  {l}
                  {texts[l]?.trim() && <span className="text-xs text-amber-600">{t("build.willOverwrite")}</span>}
                </label>
              ))}
          </fieldset>
          <ErrorBox error={error} />
        </div>
      )}
    </Dialog>
  );
}
